import * as React from "react";

import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  styled,
  TextField,
} from "@mui/material";
import {
  FormState,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form/dist/types";

const Row = styled("div")<{ marginBottom?: string; marginTop?: string }>(
  ({ marginBottom = "30px", marginTop = "0px" }) => ({
    marginBottom,
    marginTop,
    display: "flex",
    justifyContent: "space-between",
  })
);

const SmallerRow = styled("div")(() => ({
  marginBottom: "10px",
  display: "flex",
  justifyContent: "space-between",
}));

interface BasicInfoInputProps {
  register: UseFormRegister<any>;
  clearErrors: UseFormClearErrors<any>;
  formState: FormState<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  template: Template;
  blueprintName: string;
  blueprints: WorkflowBlueprintDesc[];
  setBlueprintId: React.Dispatch<React.SetStateAction<number>>;
  source: string;
}

export const BasicInfoInput: React.FC<BasicInfoInputProps> = ({
  register,
  clearErrors,
  formState,
  setValue,
  watch,
  template,
  blueprints,
  setBlueprintId,
  blueprintName,
  source,
}) => {
  //check to see if the blueprint that's being passed in is available in the list
  const availableBlueprint = blueprints?.find((blueprint) => {
    return blueprint.name === blueprintName;
  });
  const [syncHostnameSelected, setSyncHostnameSelected] = React.useState(false);
  const [selectedBlueprint, setSelectedBlueprint] = React.useState(() => {
    return availableBlueprint?.name || "";
  });

  const blueprintSelectAvailable = source === "update-template" || source === "create-template";
  const hostnameSelectAvailable = source !== "update-template" && source !== "create-template";

  const getBlueprintError = (): boolean => {
    if (formState?.errors?.blueprint) {
      return true;
    }

    if (blueprints?.length === 0) {
      return true;
    }

    //no errors should be seen if creating a new org template and there are blueprints available
    if (source === "create-template" && blueprints?.length > 0) {
      return false;
    }

    return !availableBlueprint && selectedBlueprint === template?.blueprintName;
  };
  const workflowName = watch("name");
  //when checkbox is selected and workflow name has value, show different input help text
  const hostnameHelpText =
    syncHostnameSelected && workflowName
      ? "Hostname synced with Workflow Name. Uncheck Sync checkbox to edit."
      : "Provide a hostname for your workflow (e.g. <hostname>.metworx.com)";

  React.useEffect(() => {
    if (syncHostnameSelected && workflowName) {
      //set hostname to workflow name, revalidate for errors
      setValue("hostname", workflowName.toLowerCase(), { shouldValidate: true });
    }
  }, [workflowName, setValue, syncHostnameSelected]);

  const handleSyncHostName = () => {
    setSyncHostnameSelected(!syncHostnameSelected);
  };

  return (
    <>
      <Row marginBottom="20px">
        <TextField
          fullWidth
          variant="outlined"
          id="workflow-name"
          inputProps={{
            "data-testid": "workflow-name",
          }}
          label="Workflow Name *"
          helperText={
            formState.errors?.name?.message ||
            "Must start with a letter, be between 4 and 40 characters, and can contain letters or numbers (no spaces)"
          }
          {...register("name")}
          error={!!formState?.errors?.name}
        />
      </Row>
      {blueprintSelectAvailable && (
        <>
          <SmallerRow>
            {!blueprints && (
              <Alert severity="error">
                There are no blueprints available for your org. Please contact your organization administrator for
                assistance.
              </Alert>
            )}
          </SmallerRow>
          <Row marginBottom="10px">
            <FormControl
              sx={{
                width: "100%",
              }}
              error={getBlueprintError()}
            >
              <InputLabel id="bluerint-label">Blueprint *</InputLabel>
              <Select
                id="blueprint"
                inputProps={{
                  "data-testid": "blueprint",
                  inputRef: (ref) => {
                    if (!ref) return;
                    register("blueprint");
                  },
                }}
                label="Blueprint *"
                labelId="blueprint-label"
                value={selectedBlueprint}
                disabled={false}
                onChange={(e) => {
                  setSelectedBlueprint(e.target.value);
                  setValue("blueprint", e.target.value);
                  if (e.target.value) {
                    clearErrors("blueprint");
                  }
                }}
              >
                {blueprints?.length > 0 &&
                  blueprints.map((blueprint) => (
                    <MenuItem
                      key={blueprint.id}
                      value={blueprint.name}
                      onClick={() => {
                        setSelectedBlueprint(blueprint.name);
                        setBlueprintId(blueprint.id);
                        setValue("blueprint", blueprint.name);
                      }}
                    >
                      {blueprint.name}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>
                {formState.errors?.blueprint
                  ? formState.errors?.blueprint?.message
                  : blueprints?.length > 0
                  ? "Select Blueprint"
                  : "No Blueprints Available"}
              </FormHelperText>
            </FormControl>
          </Row>
        </>
      )}
      {hostnameSelectAvailable && (
        <>
          <Row marginBottom="0px" marginTop="20px">
            <TextField
              fullWidth
              id="hostname"
              variant="outlined"
              inputProps={{
                "data-testid": "hostname",
              }}
              disabled={syncHostnameSelected}
              label="Hostname"
              helperText={formState.errors?.hostname?.message || hostnameHelpText}
              {...register("hostname")}
              InputLabelProps={{ shrink: !!watch("hostname") }}
              error={!!formState?.errors?.hostname}
            />
          </Row>

          <Row marginBottom="20px">
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    id="sync_workflow_hostname"
                    data-testid="sync_workflow_hostname"
                    checked={syncHostnameSelected}
                    onChange={handleSyncHostName}
                  />
                }
                label="Sync Hostname with Workflow Name"
                sx={{ mr: 0 }}
              />
            </FormGroup>
          </Row>
        </>
      )}
    </>
  );
};
